import { useCallback, useEffect, useRef, useState } from "react";

export const GATE_RENDER_LOOP_LIMIT = 40;
const WINDOW_MS = 1000;

export const useRenderLoopGuard = (values: Record<string, unknown>, onDetected?: (details: { renders: number; changed: string[] }) => void) => {
    const [renderLoopDetected, setRenderLoopDetected] = useState(false);
    const previousRef = useRef<Record<string, unknown> | null>(null);
    const windowRef = useRef({ started: Date.now(), renders: 0 });
    const reportedRef = useRef(false);
    const pendingRef = useRef(false);
    const detailsRef = useRef<{ renders: number; changed: string[] } | null>(null);

    const now = Date.now();
    if (now - windowRef.current.started >= WINDOW_MS) windowRef.current = { started: now, renders: 0 };
    windowRef.current.renders += 1;
    const changed = previousRef.current
        ? Object.keys(values).filter((name) => previousRef.current?.[name] !== values[name])
        : [];
    previousRef.current = values;
    if (!renderLoopDetected && !reportedRef.current && windowRef.current.renders > GATE_RENDER_LOOP_LIMIT) {
        reportedRef.current = true;
        pendingRef.current = true;
        detailsRef.current = { renders: windowRef.current.renders, changed };
        console.error("GATE_RENDER_LOOP", { renders: windowRef.current.renders, changed });
    }

    useEffect(() => {
        if (pendingRef.current) {
            pendingRef.current = false;
            if (detailsRef.current) onDetected?.(detailsRef.current);
            setRenderLoopDetected(true);
        }
    });

    const resetRenderLoopGuard = useCallback(() => {
        windowRef.current = { started: Date.now(), renders: 0 };
        previousRef.current = null;
        reportedRef.current = false;
        pendingRef.current = false;
        detailsRef.current = null;
        setRenderLoopDetected(false);
    }, []);

    return { renderLoopDetected, resetRenderLoopGuard };
};
